import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
} from '@angular/core';
import { LoadingComponent } from '@rivet-health/design-system';
import { HookDirective } from '@rivethealth/client/src/common/core/hook';
import { useEffect } from '@rivethealth/client/src/common/core/hooks';
import * as _ from 'lodash';
import { Loadable } from '../helpers/loadable';
import { errorMessage } from './load';
import { LoadableErrorService } from './loadable-error.service';

export const fadeTransitions = [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('150ms ease-in', style({ opacity: 1 })),
  ]),
  transition(':leave', [animate('150ms ease-out', style({ opacity: 0 }))]),
];

@Component({
  animations: [trigger('fade', fadeTransitions)],
  selector: 'loadable',
  styleUrls: ['./loadable.component.scss'],
  templateUrl: './loadable.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadableComponent extends HookDirective {
  constructor(
    _cdr: ChangeDetectorRef,
    private readonly loadableErrorService: LoadableErrorService,
    private ref: ElementRef,
  ) {
    super(_cdr);
  }

  @Input() data: Loadable<any> | Loadable<any>[];
  @Input() size: LoadingComponent.Size = 40;
  @Input() isErrorCoverRowStyle = false;
  @Input() disableErrorCover = false;
  @Input() customErrorMessage: string;

  get _() {
    const loadables: Loadable<any>[] = Array.isArray(this.data)
      ? this.data
      : [this.data];

    const loading: boolean = loadables.some(d => d.loading);
    const data_: boolean = loadables.every(d => d.data !== null);
    const erred: boolean = loadables.some(d => d.erred);
    const errorMessage_: string = errorMessage(
      "We're having issues loading.",
      _.find(loadables, l => !!l.error)?.error,
      !this.isErrorCoverRowStyle,
      this.customErrorMessage,
    );

    useEffect(() => {
      if (erred) {
        this.loadableErrorService.broadcastError(this.parentComponentName);
      }
    }, [erred]);

    return {
      data_,
      erred,
      errorMessage_,
      loading,
    };
  }

  get parentComponentName() {
    let el = (this.ref.nativeElement as HTMLElement).parentElement;
    while (el && this.commonTagNames.includes(el.tagName.toLowerCase())) {
      el = el.parentElement;
    }
    // `tagName` is all-caps for some reason, so we make it look normal
    return el?.tagName.toLowerCase() || '';
  }

  readonly commonTagNames = [
    'a',
    'abbr',
    'address',
    'area',
    'article',
    'aside',
    'audio',
    'b',
    'base',
    'bdi',
    'bdo',
    'blockquote',
    'body',
    'br',
    'button',
    'canvas',
    'caption',
    'cite',
    'code',
    'col',
    'colgroup',
    'data',
    'datalist',
    'dd',
    'del',
    'details',
    'dfn',
    'dialog',
    'div',
    'dl',
    'dt',
    'em',
    'embed',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'head',
    'header',
    'hr',
    'html',
    'i',
    'iframe',
    'img',
    'input',
    'ins',
    'kbd',
    'label',
    'legend',
    'li',
    'link',
    'main',
    'map',
    'mark',
    'meta',
    'meter',
    'modal',
    'modal-form',
    'nav',
    'ng-component',
    'noscript',
    'object',
    'ol',
    'optgroup',
    'option',
    'option-filter',
    'output',
    'p',
    'param',
    'picture',
    'pre',
    'progress',
    'q',
    'rp',
    'rt',
    'ruby',
    's',
    'samp',
    'script',
    'section',
    'select',
    'single-select-dropdown',
    'small',
    'source',
    'span',
    'strong',
    'style',
    'sub',
    'summary',
    'sup',
    'svg',
    'table',
    'tbody',
    'td',
    'template',
    'textarea',
    'tfoot',
    'th',
    'thead',
    'time',
    'title',
    'tr',
    'track',
    'u',
    'ul',
    'var',
    'video',
    'wbr',
  ];
}
